import { FunctionComponent } from 'react';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import type { ScriptableContext, TooltipItem, ChartType } from 'chart.js';

export type TrendLineChartPoint = {
  label: string;
  value: number;
};

export type TrendLineChartProps = {
  points: TrendLineChartPoint[];
  color?: string;
  formatValue?: (value: number) => string;
  // Tooltip body lines for a hovered point (defaults to the formatted value)
  getTooltipLines?: (pointIndex: number) => string[];
  height?: string;
};

type FC = FunctionComponent<TrendLineChartProps>;

const TrendLineChart: FC = ({
  points,
  color = '#2F82FF',
  formatValue = (value: number) => value.toLocaleString(),
  getTooltipLines,
  height = '140px',
}: TrendLineChartProps) => {
  function areaGradient(context: ScriptableContext<'line'>): string | CanvasGradient {
    const { chartArea, ctx } = context.chart;
    if (!chartArea) {
      return 'transparent';
    }
    const fill = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
    fill.addColorStop(0, `${color}33`);
    fill.addColorStop(1, `${color}00`);
    return fill;
  }

  return (
    <div style={{ height, position: 'relative' }} data-testid="trend-line-chart">
      <Line
        data={{
          labels: points.map((point) => point.label),
          datasets: [
            {
              data: points.map((point) => point.value),
              borderColor: color,
              borderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: color,
              // Surface-colored ring so overlapping points stay distinct
              pointBorderColor: '#FFFFFF',
              pointBorderWidth: 2,
              fill: true,
              backgroundColor: areaGradient,
              tension: 0,
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'nearest' as const,
            intersect: false,
          },
          scales: {
            x: {
              ticks: {
                color: '#5E5E5E',
              },
              grid: {
                display: false,
              },
            },
            y: {
              beginAtZero: true,
              ticks: {
                maxTicksLimit: 4,
                color: '#5E5E5E',
                callback: (value: string | number) => formatValue(Number(value)),
              },
              grid: {
                display: true,
                color: '#EFEFEF',
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                title(context: TooltipItem<ChartType>[]) {
                  return context[0]?.label ?? '';
                },
                label(context: TooltipItem<ChartType>) {
                  if (!getTooltipLines) {
                    return formatValue(Number(context.raw));
                  }
                  return getTooltipLines(context.dataIndex);
                },
              },
              padding: 10,
              backgroundColor: '#FBFBFB',
              titleColor: '#2D2D2D',
              bodyColor: '#2D2D2D',
              displayColors: false,
              caretSize: 6,
              borderColor: '#EFEFEF',
              borderWidth: 1,
              cornerRadius: 8,
            },
          },
        }}
      />
    </div>
  );
};

export default TrendLineChart;
